if (!customElements.get('bundle-builder')) {
    class BundleBuilder extends HTMLElement {
        static SELECTORS = {
            step: '.bundle-builder__step',
            summary: '[data-bb-summary]',
            lines: '[data-bb-lines]',
            empty: '[data-bb-empty]',
            subtotal: '[data-bb-subtotal]',
            discount: '[data-bb-discount]',
            total: '[data-bb-total]',
            save: '[data-bb-save]',
            saveAmount: '[data-bb-save-amount]',
            add: '[data-bb-add]',
            bar: '[data-bb-bar]',
            barThumbs: '[data-bb-thumbs]',
            barStatus: '[data-bb-status]',
            barTotal: '[data-bb-bar-total]',
            progress: '[data-bb-progress]',
            progressFill: '[data-bb-progress-fill]',
            toggle: '[data-bb-toggle]',
            more: '[data-bb-more]',
            remove: '[data-bb-remove]',
            productCard: '[data-bb-product-card]',
            stepCount: '[data-bb-stepcount]',
            hidden: '[hidden], .is-hidden',
            bbQuickAdd: 'quick-add-product[data-bb-mode]',
            quickAddBtn: '[data-quick-add-btn]',
            quickAddBtns: '[data-quick-add-btn], [data-quick-add-btn-mobile]',
            quickAddVariant: '[data-quick-add-variant]',
            quickAddModal: '[data-quick-add-modal], .product-quick-add',
            openQuickAddModal: '[data-quick-add-modal].is-open, .product-quick-add.is-open',
            addToCart: '[data-add-to-cart]',
            addToCartText: '[data-add-to-cart-text]',
            productLink: '[data-product-link], a.product-link',
            cardInteractive:
                'quick-add-product, [data-quick-add-holder], [data-swatch], [data-sibling-swapper], button, input, select, label, a.siblings__link',
            optionInput: 'input[type="radio"], input[type="checkbox"]',
            cartForm: 'form[action*="/cart/add"], [data-form-wrapper] form, form',
            variantIdInput: 'input[name="id"]',
            productJson: '[data-product-json]',
        };

        static MAX_BAR_THUMBS = 5;

        constructor() {
            super();

            this._onClick = (event) => this._handleClick(event);
            this._onCaptureClick = (event) => this._handleCaptureClick(event);
            this._onChange = (event) => this._handleChange(event);
            this._onItemSelected = (event) => this._addSelection(event.detail);
            this._onQuickAddOpen = () => this._restyleOpenModalCta();
            this._ctaObserver = null;
            this._modalOpenObserver = null;

            /** @type {HTMLElement | null} */
            this._bundleModalOpener = null;
        }

        connectedCallback() {
            this.sectionId = this.dataset.sectionId;
            this.discountPercent = Number(this.dataset.discountPercent) || 0;
            this.discountCode = this.dataset.discountCode || '';
            this.swatchesUrl = this.dataset.swatchesUrl || '';
            this.assetBase = this.dataset.assetBase || '';

            const { SELECTORS } = BundleBuilder;
            this.steps = this.querySelectorAll(SELECTORS.step);
            this.summary = this.querySelector(SELECTORS.summary);
            this.linesEl = this.querySelector(SELECTORS.lines);
            this.emptyEl = this.querySelector(SELECTORS.empty);
            this.subtotalEl = this.querySelector(SELECTORS.subtotal);
            this.discountEl = this.querySelector(SELECTORS.discount);
            this.totalEl = this.querySelector(SELECTORS.total);
            this.saveEl = this.querySelector(SELECTORS.save);
            this.saveAmountEl = this.querySelector(SELECTORS.saveAmount);
            this.addBtns = this.querySelectorAll(SELECTORS.add);
            this.addBtn = this.addBtns[0] || null;
            this.barEl = this.querySelector(SELECTORS.bar);
            this.barThumbsEl = this.querySelector(SELECTORS.barThumbs);
            this.barStatusEl = this.querySelector(SELECTORS.barStatus);
            this.barTotalEl = this.querySelector(SELECTORS.barTotal);
            this.progressEl = this.querySelector(SELECTORS.progress);
            this.progressFillEl = this.querySelector(SELECTORS.progressFill);
            this.totalSteps = this.steps.length;

            /** @type {Map<string, object>} */
            this.selection = new Map();

            this.addEventListener('click', this._onClick);
            this.addEventListener('change', this._onChange);

            // Capture on document so we beat theme cart handlers (bubble) and cover the modal (outside this element).
            document.addEventListener('click', this._onCaptureClick, true);
            document.addEventListener('bundle:item-selected', this._onItemSelected);
            document.addEventListener('theme:quick-add:open', this._onQuickAddOpen);

            this._renderSummary();
        }

        disconnectedCallback() {
            this.removeEventListener('click', this._onClick);
            this.removeEventListener('change', this._onChange);

            document.removeEventListener('click', this._onCaptureClick, true);
            document.removeEventListener('bundle:item-selected', this._onItemSelected);
            document.removeEventListener('theme:quick-add:open', this._onQuickAddOpen);

            this._teardownModalObservers();
        }

        // ── Event routing ────────────────────────────────────────────────────────

        /**
         * Capture-phase: intercept bundle quick-add before theme cart handlers.
         */
        _handleCaptureClick(event) {
            const { SELECTORS } = BundleBuilder;
            const bbQuickAdd = event.target.closest(SELECTORS.bbQuickAdd);
            const quickAddBtn = event.target.closest(SELECTORS.quickAddBtns);

            if (bbQuickAdd && quickAddBtn) {
                this._bundleModalOpener = bbQuickAdd;

                // Single-variant path: theme would fire cart:add-to-cart — take over instead.
                if (quickAddBtn.hasAttribute('data-quick-add-variant') || bbQuickAdd.querySelector(SELECTORS.quickAddVariant)) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    this._selectFromOpener(bbQuickAdd);
                    this._resetQuickAddLoading(bbQuickAdd);
                    return;
                }

                // Multi-variant: let the modal open; ATC is intercepted below.
                return;
            }

            const atc = event.target.closest(SELECTORS.addToCart);
            if (!atc || !this._bundleModalOpener) return;

            const modal = atc.closest(SELECTORS.quickAddModal);
            if (!modal) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            this._selectFromModal(modal, this._bundleModalOpener);
        }

        _handleClick(event) {
            const { SELECTORS } = BundleBuilder;
            const toggle = event.target.closest(SELECTORS.toggle);
            if (toggle) {
                this._toggleStep(toggle);
                return;
            }

            const more = event.target.closest(SELECTORS.more);
            if (more) {
                this._showMore(more);
                return;
            }

            if (event.target.closest(SELECTORS.add)) {
                this._addToCart(event.target.closest(SELECTORS.add));
                return;
            }

            const remove = event.target.closest(SELECTORS.remove);
            if (remove) {
                const key = remove.dataset.bbRemove;
                const removed = this.selection.get(key);
                this.selection.delete(key);
                if (removed?.step) this._updateStepCount(removed.step);
                this._renderSummary();
                return;
            }

            this._handleProductCardClick(event);
        }

        /**
         * In bundle mode, product card links open quick-add / modal instead of the PDP.
         */
        _handleProductCardClick(event) {
            const { SELECTORS } = BundleBuilder;
            const card = event.target.closest(SELECTORS.productCard);
            if (!card) return;

            // Let quick-add, swatches, siblings, wishlist, and form controls work normally.
            if (event.target.closest(SELECTORS.cardInteractive)) {
                return;
            }

            const productLink = event.target.closest(SELECTORS.productLink);
            if (!productLink) return;

            event.preventDefault();

            const quickAddBtn = card.querySelector(SELECTORS.quickAddBtn);
            if (quickAddBtn) {
                quickAddBtn.click();
            }
        }

        _handleChange(event) {
            const input = event.target.closest(BundleBuilder.SELECTORS.optionInput);
            if (!input) return;

            this._onOptionChange(input);
        }

        // ── Steps ────────────────────────────────────────────────────────────────

        _toggleStep(button) {
            const step = button.closest(BundleBuilder.SELECTORS.step);
            if (!step) return;

            const collapsed = step.classList.toggle('is-collapsed');
            button.setAttribute('aria-expanded', String(!collapsed));
        }

        _showMore(button) {
            const { SELECTORS } = BundleBuilder;
            const step = button.closest(SELECTORS.step);
            if (!step) return;

            step.querySelectorAll(SELECTORS.hidden).forEach((el) => {
                el.hidden = false;
                el.classList.remove('is-hidden');
            });
            button.hidden = true;
        }

        // ── Selection ────────────────────────────────────────────────────────────

        _onOptionChange(input) {
            // Legacy option-card path (if present alongside quick-add).
            this._renderSummary();
        }

        _selectFromOpener(opener) {
            const item = this._itemFromOpener(opener);
            if (!item?.variantId) return;

            this._emitItemSelected(item);
        }

        _selectFromModal(modal, opener) {
            const { SELECTORS } = BundleBuilder;
            const form = modal.querySelector(SELECTORS.cartForm);
            const idInput = form?.querySelector(SELECTORS.variantIdInput);
            const variantId = idInput?.value;
            if (!variantId) return;

            const productJsonEl = modal.querySelector(SELECTORS.productJson);
            let product = null;
            let variant = null;

            try {
                product = productJsonEl ? JSON.parse(productJsonEl.textContent) : null;
                variant = product?.variants?.find((v) => String(v.id) === String(variantId)) || null;
            } catch (error) {
                console.warn('[bundle-builder] Failed to parse product JSON', error);
            }

            const openerItem = this._itemFromOpener(opener);
            const image =
                (typeof variant?.featured_image === 'string'
                    ? variant.featured_image
                    : variant?.featured_image?.src) ||
                (typeof product?.featured_image === 'string'
                    ? product.featured_image
                    : product?.featured_image?.src) ||
                openerItem.image ||
                '';

            const item = {
                ...openerItem,
                variantId: String(variantId),
                productId: String(product?.id || openerItem.productId || ''),
                handle: product?.handle || openerItem.handle || '',
                title: (product?.title || openerItem.title || '').split('||')[0].trim(),
                price: Number(variant?.price ?? openerItem.price) || 0,
                image,
                options: variant?.options || [],
                quantity: 1,
            };

            this._emitItemSelected(item);
            this._closeQuickAddModal(modal);
        }

        _itemFromOpener(opener) {
            return {
                step: opener.dataset.bbStep || '',
                productId: opener.dataset.bbProductId || '',
                variantId: opener.dataset.bbVariantId || opener.dataset.variant || '',
                handle: opener.dataset.bbHandle || opener.dataset.productHandle || '',
                title: opener.dataset.bbTitle || '',
                price: Number(opener.dataset.bbPrice) || 0,
                image: opener.dataset.bbImage || '',
                quantity: 1,
            };
        }

        _emitItemSelected(item) {
            document.dispatchEvent(
                new CustomEvent('bundle:item-selected', {
                    bubbles: true,
                    detail: item,
                })
            );
        }

        _addSelection(item) {
            if (!item?.variantId) return;

            const stepEl = item.step ? this.querySelector(`${BundleBuilder.SELECTORS.step}[data-step="${item.step}"]`) : null;
            const selectionType = stepEl?.dataset.selection || 'single';
            const key =
                selectionType === 'multi'
                    ? `step-${item.step}-${item.variantId}`
                    : `step-${item.step || item.variantId}`;

            this.selection.set(key, { ...item, key });
            this._updateStepCount(item.step);
            this._renderSummary();
        }

        _updateStepCount(step) {
            if (!step) return;
            const { SELECTORS } = BundleBuilder;
            const stepEl = this.querySelector(`${SELECTORS.step}[data-step="${step}"]`);
            if (!stepEl) return;

            const countEl = stepEl.querySelector(SELECTORS.stepCount);
            if (!countEl) return;

            const count = [...this.selection.values()].filter((item) => String(item.step) === String(step)).length;
            countEl.textContent = count > 0 ? String(count) : '';
        }

        // ── Modal helpers ────────────────────────────────────────────────────────

        _getOpenQuickAddModal() {
            return document.querySelector(BundleBuilder.SELECTORS.openQuickAddModal);
        }

        _restyleOpenModalCta() {
            if (!this._bundleModalOpener) return;

            const modal = this._getOpenQuickAddModal();
            if (!modal) return;

            modal.setAttribute('data-bb-selecting', '');
            this._applyBundleCtaLabel(modal);

            this._ctaObserver?.disconnect();
            this._ctaObserver = new MutationObserver(() => this._applyBundleCtaLabel(modal));
            this._ctaObserver.observe(modal, { childList: true, subtree: true, characterData: true });

            this._modalOpenObserver?.disconnect();
            this._modalOpenObserver = new MutationObserver(() => {
                if (!modal.classList.contains('is-open')) {
                    this._clearBundleModalState(modal);
                }
            });
            this._modalOpenObserver.observe(modal, { attributes: true, attributeFilter: ['class'] });
        }

        _applyBundleCtaLabel(modal) {
            const { SELECTORS } = BundleBuilder;
            const text = modal.querySelector(SELECTORS.addToCartText);
            if (text && text.textContent.trim() !== 'Add to bundle') {
                text.textContent = 'Add to bundle';
            }

            const atc = modal.querySelector(SELECTORS.addToCart);
            if (atc) atc.setAttribute('data-bb-select', '');
        }

        _clearBundleModalState(modal) {
            modal?.removeAttribute('data-bb-selecting');
            this._bundleModalOpener = null;
            this._teardownModalObservers();
        }

        _teardownModalObservers() {
            this._ctaObserver?.disconnect();
            this._ctaObserver = null;
            this._modalOpenObserver?.disconnect();
            this._modalOpenObserver = null;
        }

        _closeQuickAddModal(modal) {
            const modalId = modal?.id;
            const MicroModal = window.themeVendor?.MicroModal;

            if (MicroModal && modalId) {
                MicroModal.close(modalId);
            } else {
                modal?.classList.remove('is-open');
                modal?.setAttribute('aria-hidden', 'true');
            }

            this._clearBundleModalState(modal);
        }

        _resetQuickAddLoading(opener) {
            opener.querySelectorAll(BundleBuilder.SELECTORS.quickAddBtns).forEach((btn) => {
                btn.classList.remove('is-loading', 'is-added', 'has-error');
                btn.disabled = false;
            });
        }

        // ── Summary ──────────────────────────────────────────────────────────────

        _renderSummary() {
            const items = [...this.selection.values()];
            const hasItems = items.length > 0;

            if (this.emptyEl) this.emptyEl.hidden = hasItems;
            if (this.linesEl) {
                this.linesEl.hidden = !hasItems;
                this.linesEl.innerHTML = items
                    .map(
                        (item) => `
                    <li class="bundle-builder__line">
                      <div class="bundle-builder__line-main">
                        <span class="bundle-builder__line-title">${this._escapeHtml(item.title)}</span>
                        <span class="bundle-builder__line-price">${this._formatMoney(item.price)}</span>
                      </div>
                      <button type="button" class="bundle-builder__line-remove" data-bb-remove="${this._escapeHtml(item.key)}" aria-label="Remove ${this._escapeHtml(item.title)}">Remove</button>
                    </li>`
                    )
                    .join('');
            }

            const subtotal = items.reduce((sum, item) => sum + (Number(item.price) || 0) * (item.quantity || 1), 0);
            const discount = items.length >= 2 ? Math.round(subtotal * (this.discountPercent / 100)) : 0;
            const total = Math.max(subtotal - discount, 0);

            if (this.subtotalEl) this.subtotalEl.textContent = this._formatMoney(subtotal);
            if (this.discountEl) this.discountEl.textContent = `-${this._formatMoney(discount)}`;
            if (this.totalEl) this.totalEl.textContent = this._formatMoney(total);

            if (this.saveEl) {
                this.saveEl.hidden = discount <= 0;
                if (this.saveAmountEl && discount > 0) {
                    this.saveAmountEl.textContent = this._formatMoney(discount);
                }
            }

            this.addBtns?.forEach((btn) => {
                btn.disabled = !hasItems;
            });

            this._renderBar(items, total);
        }

        _renderBar(items, total) {
            const stepsTouched = new Set(
                items.map((item) => String(item.step)).filter(Boolean)
            ).size;
            const totalSteps = this.totalSteps || 0;
            const left = Math.max(totalSteps - stepsTouched, 0);
            const percent = totalSteps > 0 ? Math.round((stepsTouched / totalSteps) * 100) : 0;

            if (this.progressFillEl) {
                this.progressFillEl.style.width = `${percent}%`;
            }
            if (this.progressEl) {
                this.progressEl.setAttribute('aria-valuenow', String(stepsTouched));
                this.progressEl.setAttribute('aria-valuemax', String(totalSteps));
                this.progressEl.setAttribute(
                    'aria-valuetext',
                    totalSteps === 0
                        ? 'No steps'
                        : left === 0
                          ? `All ${totalSteps} steps complete`
                          : `${stepsTouched} of ${totalSteps} steps, ${left} left`
                );
            }

            if (this.barStatusEl) {
                if (totalSteps === 0) {
                    this.barStatusEl.textContent = 'Add items to your bundle';
                } else if (left === 0) {
                    this.barStatusEl.textContent = `All ${totalSteps} steps · complete`;
                } else {
                    this.barStatusEl.textContent = `${stepsTouched} of ${totalSteps} steps · ${left} left`;
                }
            }

            if (this.barTotalEl) {
                this.barTotalEl.textContent = this._formatMoney(total);
            }

            if (this.barThumbsEl) {
                const max = BundleBuilder.MAX_BAR_THUMBS;
                const visible = items.slice(0, max);
                const overflow = items.length - visible.length;

                this.barThumbsEl.innerHTML =
                    visible
                        .map((item) => {
                            const title = this._escapeHtml(item.title || 'Selected item');
                            if (item.image) {
                                return `<span class="bundle-builder__bar-thumb"><img src="${this._escapeHtml(item.image)}" alt="${title}" width="40" height="40" loading="lazy"></span>`;
                            }
                            return `<span class="bundle-builder__bar-thumb" aria-label="${title}"></span>`;
                        })
                        .join('') +
                    (overflow > 0
                        ? `<span class="bundle-builder__bar-thumb bundle-builder__bar-thumb--more" aria-label="${overflow} more items">+${overflow}</span>`
                        : '');
            }
        }

        // ── Cart ─────────────────────────────────────────────────────────────────

        /**
         * Add the current selection as one cart bundle (shared `_bundle_id` + properties).
         * Uses `window.cart.addToCart` when available; 
         */
        async _addToCart(triggerBtn = null) {
            if (this._adding) return;

            const selected = [...this.selection.values()];
            if (!selected.length) return;
            if (!this._validateRequiredSteps()) return;

            const bundleId = this._createBundleId();
            const items = selected.map((item) => ({
                id: Number(item.variantId),
                quantity: Number(item.quantity) || 1,
                properties: {
                    _sc_bundle_id: bundleId,
                    _bundle_type: 'bundle_builder',
                },
            }));
            const payload = JSON.stringify({ items });
            const button = triggerBtn || this.addBtn;

            // Theme cart drawer owns loading/disabled state for this path.
            if (typeof window.cart?.addToCart === 'function') {
                window.cart.addToCart(payload, null, button);
                return;
            }

            this._adding = true;

        }

        /**
         * @returns {string}
         */
        _createBundleId() {
            if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
                return `scbd_${crypto.randomUUID()}`;
            }
            return `scbd_${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        }

        /**
         * @returns {boolean}
         */
        _validateRequiredSteps() {
            for (const stepEl of this.steps) {
                if (stepEl.dataset.required !== 'true') continue;

                const step = stepEl.dataset.step;
                const hasSelection = [...this.selection.values()].some(
                    (item) => String(item.step) === String(step)
                );
                if (hasSelection) continue;

                stepEl.classList.remove('is-collapsed');
                const toggle = stepEl.querySelector(BundleBuilder.SELECTORS.toggle);
                if (toggle) toggle.setAttribute('aria-expanded', 'true');
                stepEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return false;
            }
            return true;
        }



        /**
         * @param {string} payload
         * @returns {Promise<object>}
         */
        async _postCartAdd(payload) {
            const root = window.theme?.routes?.root || '/';
            const response = await fetch(`${root}cart/add.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: payload,
            });
            return response.json();
        }

        /**
         * @param {string} message
         */
        _setAddButtonError(message) {
            this.addBtns?.forEach((btn) => {
                btn.classList.remove('is-loading');
                btn.classList.add('has-error');
                btn.disabled = false;
                btn.setAttribute('aria-invalid', 'true');
                btn.title = message;
            });

            window.setTimeout(() => {
                this.addBtns?.forEach((btn) => {
                    btn.classList.remove('has-error');
                    btn.removeAttribute('aria-invalid');
                    btn.removeAttribute('title');
                });
            }, 4000);
        }

        // ── Helpers ──────────────────────────────────────────────────────────────

        _formatMoney(cents) {
            if (window.themeVendor?.themeCurrency) {
                return window.themeVendor.themeCurrency.formatMoney(cents, window.theme.moneyFormat);
            }
            return `$${(cents / 100).toFixed(2)}`;
        }

        _escapeHtml(value) {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        }
    }

    customElements.define('bundle-builder', BundleBuilder);
}
